import type {QuestionModelV3} from '@albert-io/models';
import cx from 'classnames';
import React, {useCallback, useLayoutEffect, useRef} from 'react';
import {DroppableItem} from './BankItems.react';
import useQuestionStore from 'client/components/QuestionTypes/V2/useQuestionStores';
import {useMathJaxOverflow} from '../../MultipleChoice/useMathJaxOverflow';

interface Props {
  question: QuestionModelV3;
  handleItemClick: (itemId: string) => void;
  handleSlotClick: (slotId: string) => void;
  handleResetItem: (itemId: string) => void;
}
const DropSlots = ({question, handleItemClick, handleSlotClick, handleResetItem}: Props) => {
  const useStoreHook = useQuestionStore<'item-bank'>(question);
  // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const dropSlots = useStoreHook((state) => state.dropSlots);
  const {ref: mathJaxOverflowRef, hasMathJaxOverflow} = useMathJaxOverflow();
  const valueRendererRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const registerValueRenderer = useCallback((slotId: string, el: HTMLDivElement | null) => {
    if (el) {
      valueRendererRefs.current.set(slotId, el);
    } else {
      valueRendererRefs.current.delete(slotId);
    }
  }, []);

  // Align all valueRenderer sections to the same height
  useLayoutEffect(() => {
    const elements = Array.from(valueRendererRefs.current.values());
    if (elements.length === 0) return;

    const align = () => {
      elements.forEach((el) => (el.style.minHeight = ''));
      let maxHeight = 0;
      elements.forEach((el) => {
        maxHeight = Math.max(maxHeight, el.scrollHeight);
      });
      elements.forEach((el) => {
        el.style.minHeight = `${maxHeight}px`;
      });
    };

    align();

    // Re-align when images inside value renderers finish loading
    const images = elements.flatMap((el) => Array.from(el.querySelectorAll('img')));
    images.forEach((img) => img.addEventListener('load', align));

    return () => {
      images.forEach((img) => img.removeEventListener('load', align));
    };
  });

  return (
    <div
      ref={mathJaxOverflowRef}
      className={cx('mcq__options-grid mt-x4', {
        'mcq__options-grid--mathjax-overflow': hasMathJaxOverflow
      })}
    >
      {dropSlots.map((slot) => {
        return (
          <DroppableItem
            key={slot.id}
            slot={slot}
            useStoreHook={useStoreHook}
            onClick={() => handleSlotClick(slot.id)}
            onItemClick={handleItemClick}
            onResetItem={handleResetItem}
            registerValueRenderer={registerValueRenderer}
          />
        );
      })}
    </div>
  );
};
export default DropSlots;
